import { ReservationStatusErrors, RetryOptions } from '@enums'
import { ApiError } from '@lib/api'

interface Params<TParams, TResponse> {
  fn: (params: TParams) => Promise<TResponse>
  validate: (result: TResponse) => boolean
  interval: number
  maxAttempts: number
  ignoreFailure?: boolean
}

const isShopperError = (error: ApiError): boolean => {
  if (!Array.isArray(error.data.paymentErrors)) return false

  return error.data.paymentErrors[0]?.responsibility === ReservationStatusErrors.Shopper
}

export class MaxAttemptsError extends Error {
  constructor() {
    super('Max attempts exceeded')
  }
}

export class ShopperError extends Error {
  public readonly confirmationOnly: boolean
  public readonly reservationId: string

  constructor(error: ApiError) {
    super('Shopper error')

    this.confirmationOnly = error.data.retryOption === RetryOptions.ConfirmationOnly
    this.reservationId = error.data.reservationId as string
  }
}

export type PollingApiError = ApiError | MaxAttemptsError

const polling = async <TParams, TResponse>({
  fn,
  validate,
  interval,
  maxAttempts,
  ignoreFailure,
}: Params<TParams, TResponse>) => {
  return async (params: TParams): Promise<TResponse> => {
    let attempts = 0

    const executePoll = (resolve: any, reject: any): void => {
      attempts++

      fn(params)
        .then(result => {
          if (validate(result)) return resolve(result)
          scheduleRetry()
        })
        .catch(err => {
          if (ignoreFailure) {
            scheduleRetry()
            return
          }
          if (isShopperError(err)) return reject(new ShopperError(err))
          return reject(err)
        })

      const scheduleRetry = () => {
        if (maxAttempts && attempts === maxAttempts) return reject(new MaxAttemptsError())
        setTimeout(executePoll, interval, resolve, reject)
      }
    }

    return await new Promise(executePoll)
  }
}

export default polling
